import { Eraser, Plus, Undo2, Wand2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Cell, Maze, Plan, Waypoints } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { cn } from "@/lib/utils";

import { rowY, viewBox } from "./maze-geometry";
import { FLOOR, MarkerLegend, Markers, Walls } from "./maze-svg";

/** Route colours, one per drawn route, cycling. Chosen to read on the floor. */
const ROUTE_COLOURS = ["#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c", "#0891b2", "#be185d", "#4d7c0f"];

function sameCell(a: Cell, b: Cell): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Draw exploration routes on the maze, the way `record_exploration_v2.py` did in an OpenCV
 * window: click free cells to add waypoints, right-click to undo, one route at a time.
 *
 * Every edit is sent to `plan`, which returns the A* paths and a per-cell visit count in
 * milliseconds; that is what shades the map. Coverage is the thing the admin is optimising --
 * a cell no route passes is a cell no student has ever seen an image of -- so it is on
 * screen at all times rather than in a number somewhere.
 *
 * The floor, walls and markers come from `maze-svg`, which also explains the row flip and why
 * walls are strokes rather than squares.
 *
 * The robot always starts at the spawn, and the recorder prefixes it to any route that does
 * not begin there. The editor does not put it into the admin's waypoints for them; instead
 * the leg the recorder will add is drawn dashed, so the waypoints are exactly what was
 * clicked and the line is exactly what will be driven.
 */
export function RouteEditor({
  maze,
  adminKey,
  value,
  onChange,
}: {
  maze: Maze;
  adminKey: string;
  value: Cell[][];
  onChange: (routes: Cell[][]) => void;
}) {
  const grid = useMemo(() => ({ ...maze.grid!, cells: maze.grid!.cells ?? [] }), [maze.grid]);
  const spawn = maze.spawn!.cell;
  const target = maze.target!.cell;
  const [active, setActive] = useState(0);
  const [planned, setPlanned] = useState<Plan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [autoCount, setAutoCount] = useState(3);
  const [autoBusy, setAutoBusy] = useState(false);
  const [hover, setHover] = useState<Cell | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const waypoints: Waypoints | null = useMemo(() => {
    const routes = value.filter((r) => r.length > 0);
    return routes.length ? { start: spawn, target, trajectories: routes } : null;
  }, [value, spawn, target]);

  // Debounced: a click is one edit; a run of fast clicks is one request.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!waypoints) return;
    // A request for an older set of waypoints may answer after a newer one: ignore it.
    let stale = false;
    timer.current = setTimeout(async () => {
      try {
        const next = await adminApi.plan(maze.id, { waypoints }, adminKey);
        if (stale) return;
        setPlanned(next);
        setPlanError(null);
      } catch (caught) {
        if (stale) return;
        setPlanError(caught instanceof ApiError ? caught.message : "plan failed");
      }
    }, 150);
    return () => {
      stale = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [waypoints, maze.id, adminKey]);
  // No waypoints, no plan -- derived, so clearing the routes needs no setState in the effect.
  const plan = waypoints ? planned : null;

  const visits = useMemo(() => {
    const map = new Map<string, number>();
    for (const [r, c, n] of plan?.coverage.counts ?? []) map.set(`${r},${c}`, n);
    return map;
  }, [plan]);
  const maxVisits = useMemo(() => Math.max(1, ...visits.values()), [visits]);

  const free = useCallback((cell: Cell) => grid.cells[cell[0]]?.[cell[1]] === ".", [grid]);

  function cellFromEvent(event: React.MouseEvent<SVGSVGElement>): Cell | null {
    const box = event.currentTarget.getBoundingClientRect();
    const col = Math.floor(((event.clientX - box.left) / box.width) * grid.cols);
    const rowFromTop = Math.floor(((event.clientY - box.top) / box.height) * grid.rows);
    const row = grid.rows - 1 - rowFromTop;
    if (row < 0 || row >= grid.rows || col < 0 || col >= grid.cols) return null;
    return [row, col];
  }

  function addWaypoint(cell: Cell) {
    if (!free(cell)) return;
    const routes = value.length ? value.map((r) => [...r]) : [[]];
    const route = routes[active] ?? (routes[active] = []);
    if (route.length && sameCell(route[route.length - 1], cell)) return;
    route.push(cell);
    onChange(routes);
  }

  function undo() {
    const routes = value.map((r) => [...r]);
    routes[active]?.pop();
    onChange(routes);
  }

  function newRoute() {
    const routes = [...value, []];
    onChange(routes);
    setActive(routes.length - 1);
  }

  function clear() {
    onChange([]);
    setActive(0);
  }

  async function autoRoutes() {
    setAutoBusy(true);
    try {
      const auto = await adminApi.plan(maze.id, { routes: autoCount }, adminKey);
      // The planner's routes start at the spawn; drop it so they look like drawn ones.
      onChange(auto.routes.map((r) => (r.waypoints.length > 1 && sameCell(r.waypoints[0], spawn) ? r.waypoints.slice(1) : r.waypoints)));
      setActive(0);
    } catch (caught) {
      setPlanError(caught instanceof ApiError ? caught.message : "plan failed");
    } finally {
      setAutoBusy(false);
    }
  }

  const y = (row: number) => rowY(grid, row);
  const cx = (cell: Cell) => cell[1] + 0.5;
  const cy = (cell: Cell) => y(cell[0]) + 0.5;

  /** Split a planned path into the leg the recorder adds (spawn to first click) and the rest. */
  function splitPath(routeIndex: number, path: Cell[]): { prefix: Cell[]; own: Cell[] } {
    const drawn = value.filter((r) => r.length > 0)[routeIndex];
    const first = drawn?.[0];
    if (!first) return { prefix: [], own: path };
    const at = path.findIndex((c) => sameCell(c, first));
    if (at <= 0) return { prefix: [], own: path };
    return { prefix: path.slice(0, at + 1), own: path.slice(at) };
  }

  const points = (cells: Cell[]) => cells.map((c) => `${cx(c)},${cy(c)}`).join(" ");

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
      <div className="space-y-2">
        <svg
          viewBox={viewBox(grid)}
          className="aspect-square w-full cursor-crosshair rounded-md border select-none"
          style={{ background: FLOOR }}
          onClick={(e) => {
            const cell = cellFromEvent(e);
            if (cell) addWaypoint(cell);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            undo();
          }}
          onMouseMove={(e) => setHover(cellFromEvent(e))}
          onMouseLeave={() => setHover(null)}
          role="img"
          aria-label="Maze map; click a free cell to add a waypoint, right-click to undo"
        >
          {/* coverage, under everything */}
          {grid.cells.map((rowText, row) =>
            Array.from(rowText).map((ch, col) => {
              if (ch !== ".") return null;
              const n = visits.get(`${row},${col}`) ?? 0;
              if (!n) return null;
              return (
                <rect
                  key={`v${row}-${col}`}
                  x={col}
                  y={y(row)}
                  width={1}
                  height={1}
                  fill={`rgba(37, 99, 235, ${0.12 + 0.45 * (n / maxVisits)})`}
                />
              );
            }),
          )}

          {/* hover */}
          {hover && free(hover) ? (
            <rect x={hover[1]} y={y(hover[0])} width={1} height={1} fill="rgba(0,0,0,0.08)" />
          ) : null}

          <Walls grid={grid} />

          {/* planned paths: the recorder's spawn leg dashed, the drawn part solid */}
          {plan?.routes.map((route) => {
            const colour = ROUTE_COLOURS[route.index % ROUTE_COLOURS.length];
            const { prefix, own } = splitPath(route.index, route.path);
            const width = route.index === active ? 0.28 : 0.16;
            return (
              <g key={route.index} opacity={route.index === active ? 1 : 0.7}>
                {prefix.length > 1 ? (
                  <polyline points={points(prefix)} fill="none" stroke={colour} strokeWidth={width * 0.7} strokeDasharray="0.35 0.3" strokeLinecap="round" strokeLinejoin="round" />
                ) : null}
                <polyline points={points(own)} fill="none" stroke={colour} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" />
              </g>
            );
          })}

          {/* waypoints, numbered */}
          {value.map((route, index) =>
            route.map((cell, k) => (
              <g key={`${index}-${k}`}>
                <circle cx={cx(cell)} cy={cy(cell)} r={0.36} fill={ROUTE_COLOURS[index % ROUTE_COLOURS.length]} stroke="white" strokeWidth={0.08} />
                <text x={cx(cell)} y={cy(cell) + 0.02} fontSize={0.42} fill="white" textAnchor="middle" dominantBaseline="middle" fontWeight={600}>
                  {k + 1}
                </text>
              </g>
            )),
          )}

          <Markers grid={grid} spawn={spawn} target={target} />
        </svg>
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <MarkerLegend />
          <span>
            <span className="mr-1 inline-block h-2.5 w-4 bg-[rgba(37,99,235,0.4)] align-middle" /> visited by routes
          </span>
          <span>dashed: the leg from the spawn the recorder adds</span>
          <span className="ml-auto font-mono">{hover ? `(${hover[0]}, ${hover[1]})` : ""}</span>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Routes</Label>
          <div className="flex flex-wrap gap-1.5">
            {(value.length ? value : [[]]).map((route, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActive(index)}
                className={cn(
                  "rounded-md border px-2 py-1 font-mono text-xs",
                  index === active ? "ring-ring ring-2" : "hover:bg-muted",
                )}
                style={{ borderColor: ROUTE_COLOURS[index % ROUTE_COLOURS.length] }}
              >
                {index + 1} · {route.length} pts
              </button>
            ))}
            <Button variant="outline" size="sm" onClick={newRoute}>
              <Plus className="size-3.5" aria-hidden />
              Route
            </Button>
          </div>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" onClick={undo} disabled={!value[active]?.length}>
              <Undo2 className="size-3.5" aria-hidden />
              Undo
            </Button>
            <Button variant="outline" size="sm" onClick={clear} disabled={!value.length}>
              <Eraser className="size-3.5" aria-hidden />
              Clear
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Click free cells to add waypoints to route {active + 1}; right-click to undo. Every
            route is driven from the spawn: the robot starts there, so the recorder adds that leg
            itself (dashed).
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="auto-count">Or plan automatically</Label>
          <div className="flex gap-1.5">
            <Input
              id="auto-count"
              type="number"
              min={1}
              max={32}
              value={autoCount}
              onChange={(e) => setAutoCount(Number(e.target.value))}
              className="w-20"
            />
            <Button variant="outline" size="sm" onClick={autoRoutes} disabled={autoBusy}>
              <Wand2 className="size-3.5" aria-hidden />
              Cover the maze
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Greedy nearest-uncovered-cell routes that together reach every free cell. You can
            edit them afterwards.
          </p>
        </div>

        <dl className="grid grid-cols-[6rem_1fr] gap-1 rounded-md border p-3 text-xs">
          <dt className="text-muted-foreground">Coverage</dt>
          <dd className="font-mono">
            {plan
              ? `${plan.coverage.covered} / ${plan.coverage.free} · ${Math.round(plan.coverage.fraction * 100)}%`
              : "—"}
          </dd>
          <dt className="text-muted-foreground">Path cells</dt>
          <dd className="font-mono">{plan ? plan.routes.reduce((sum, r) => sum + r.cells, 0) : "—"}</dd>
          {plan?.routes.some((r) => r.unreachable_legs) ? (
            <>
              <dt className="text-destructive">Unreachable</dt>
              <dd className="text-destructive">
                {plan.routes.filter((r) => r.unreachable_legs).map((r) => `route ${r.index + 1}`).join(", ")}
              </dd>
            </>
          ) : null}
        </dl>
        {planError ? <p className="text-destructive text-xs">{planError}</p> : null}
      </div>
    </div>
  );
}
